import type { SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import type { ErrorDetail } from "@shared/errors/envelope";
import type { RunKind } from "@shared/types/runs";
import type { DbtJobHistoryOutcomeKind } from "@shared/types/dbtJobHistory";
import type {
  TransformationGraph,
  TransformationGraphNode,
  TransformationGraphTest,
  DbtJobRunRow,
  DbtJobOwnRunStatus,
  DbtJobSelection,
  DbtJobView,
} from "@shared/types/transformations";
import { dbtNodeRows, isDbtFailedStatus } from "@shared/types/dbtCauses";
import { DBT_SELECTOR_CHARSET } from "@shared/types/transformations";
import { byCodeUnit } from "@shared/sort";
import { NOT_STARTED_LABEL, RUN_KIND_LABELS, isNotStarted } from "@/components/runs/runFormatting";
import { formatInstant, type InstantStyle } from "@/lib/time/displayTime";
import { failedRunWord, isKnownPhase, phasePhrase } from "@/lib/transformations/runOutcome";

/**
 * How a transformation and its runs read on screen (canvases 15 and 16; FR-62,
 * UX-5, SEC-17).
 *
 * One module for the list, the detail header, the runs grid and the tests
 * panel, so "what colour is exit class `test`" and "what does a `tag:` selector
 * attribute to" have one answer. Colour is never the only signal: every badge
 * carries its word.
 *
 * The step's outcome words on the header (Succeeded, Tests failed, Model failed, Could not
 * clone, Running, Never run) and a node's result (#1207: Succeeded, Failed, Skipped, the product's
 * one word for success, never dbt's lower-case "success") read alike. A test keeps dbt's own words
 * (pass, fail, error, warn, skipped). The step's exit class (ok, config, auth, clone, deps,
 * compile, run, test, infra, timeout, internal, cancelled, unknown) is a machine value: it chooses
 * the words, and is not printed as a label of its own.
 */

export interface Presentation {
  label: string;
  color: SkyBadgeColor;
  /** True while the row is expected to keep changing. */
  isActive: boolean;
}

/** #1251: the Runs list's word for a run's trigger (`RUN_KIND_LABELS`), so one run reads one word. */
export function kindLabel(kind: RunKind): string {
  return RUN_KIND_LABELS[kind] ?? kind;
}

/**
 * The outcome of ONE transform step in the header's words.
 *
 * A failed step is named by its exit class because the three failures an
 * operator meets most read differently: a test that failed is a data finding,
 * a model that failed is a SQL finding, and a clone that failed is a binding
 * finding — each has a different screen to fix it on.
 */
export function transformStepPresentation(row: DbtJobRunRow | null): Presentation {
  if (!row) return { label: "Never run", color: "subtle", isActive: false };
  return stepStatusPresentation(row.stepStatus, row.exitClass);
}

/**
 * `transformStepPresentation`'s words for a step's status and exit class, for a step known only
 * by those two (a dbt job's own run, #1074 review), so one run reads one word on every screen.
 */
export function stepStatusPresentation(
  stepStatus: DbtJobRunRow["stepStatus"],
  exitClass: string | null,
): Presentation {
  switch (stepStatus) {
    case "pending":
      return { label: "Queued", color: "informative", isActive: true };
    case "running":
      return { label: "Running", color: "informative", isActive: true };
    case "succeeded":
      return { label: "Succeeded", color: "success", isActive: false };
    case "skipped":
      return { label: "Skipped", color: "warning", isActive: false };
    case "cancelled":
      return { label: "Cancelled", color: "subtle", isActive: false };
    case "failed":
      break;
  }
  // #1207 round 3: one failed word with `runOutcome` (`failedRunWord`), never the exit class
  // itself ("Failed (deps)"); the cause line says why.
  return { label: failedRunWord(exitClass), color: "danger", isActive: false };
}

/**
 * A node's last-run result in the product's words, and its colour (#1207: "Succeeded" as on the
 * strip, the Jobs view and the Models view, never dbt's "success"). Every status in dbt's closed
 * set (`DBT_RESULT_STATUSES`) has a word: the failed ones (`isDbtFailedStatus`, which counts
 * "runtime error" and "partial success") read Failed, as they count everywhere else, and a status
 * outside the set reads "Status not recognised", never dbt's raw lower-case word.
 */
export function nodeStatusPresentation(status: string | null): Presentation | null {
  if (status === null) return null;
  if (status === "success" || status === "pass") {
    return { label: "Succeeded", color: "success", isActive: false };
  }
  if (isDbtFailedStatus(status)) return { label: "Failed", color: "danger", isActive: false };
  if (status === "skipped") return { label: "Skipped", color: "warning", isActive: false };
  if (status === "warn") return { label: "Warned", color: "warning", isActive: false };
  if (status === "no-op") return { label: "No change", color: "subtle", isActive: false };
  return { label: "Status not recognised", color: "subtle", isActive: false };
}

/**
 * #1207 round 1: what an active dbt run is doing, in words, from dbt's phase token ("deps",
 * "test"), never the token itself. A step that has begun but reported no phase yet is Starting.
 * Round 2: the words are `runOutcome`'s one map (`phasePhrase`), capitalised, so a phase reads
 * the same while the run is active and once it stopped.
 */
export function phaseWords(phase: string | null): string {
  if (!phase) return "Starting";
  if (!isKnownPhase(phase)) return "Running";
  const phrase = phasePhrase(phase);
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/**
 * #1207 round 1: the Runs tab's line under an active run's badge. A queued run is Waiting to
 * start; a running one says its phase in words, so "Running" never sits over "Waiting to start".
 */
export function activeStepLine(row: Pick<DbtJobRunRow, "stepStatus" | "phase">): string {
  return row.stepStatus === "pending" ? "Waiting to start" : phaseWords(row.phase);
}

/** dbt's own word for a test's status (`pass`, `fail`, `error`, `warn`, `skipped`). */
export function testStatusPresentation(status: string | null): Presentation {
  if (status === null) return { label: "not run", color: "subtle", isActive: false };
  if (status === "pass") return { label: "pass", color: "success", isActive: false };
  if (status === "fail" || status === "error") {
    return { label: status, color: "danger", isActive: false };
  }
  return { label: status, color: "subtle", isActive: false };
}

/**
 * A step's tests as "passed / failed". `testsSucceeded` comes from the image's
 * summary and may be missing (storage unreadable) while the step's own
 * `testsFailed` is not: then the passed side reads as a dash, never as 0.
 */
export function testsPair(row: DbtJobRunRow): string {
  if (row.testsSucceeded === null && row.testsFailed === null) return "—";
  // Never derived from total − passed: `testsTotal` includes skipped tests (review N-4).
  return `${row.testsSucceeded ?? "—"} / ${row.testsFailed ?? "—"}`;
}

/**
 * Tests that neither passed nor failed (skipped after a failed dependency), when all three
 * counts are known and there are any; null otherwise. `testsTotal` includes them (review N-3).
 */
export function testsSkipped(row: DbtJobRunRow): number | null {
  if (row.testsTotal === null || row.testsSucceeded === null || row.testsFailed === null) {
    return null;
  }
  const skipped = row.testsTotal - row.testsSucceeded - row.testsFailed;
  return skipped > 0 ? skipped : null;
}

/**
 * dbt's materialization as a label (round 4, owner 2026-09-30: "Materialized should show Table
 * and not table"): the first letter capitalised and underscores as spaces ("table" is "Table",
 * "materialized_view" is "Materialized view"). The raw value stays the cell's title, the word a
 * dbt author writes in `+materialized`.
 */
export function materializationLabel(value: string): string {
  const words = value.replace(/_/g, " ").trim();
  return words ? `${words[0]!.toUpperCase()}${words.slice(1)}` : value;
}

/** `1.9 s`, `0.41 s`; null reads as an em dash. */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
  return `${seconds < 10 ? seconds.toFixed(2).replace(/0$/, "") : Math.round(seconds)} s`;
}

/** `800 rows`, `1 row`; null reads as an em dash. A count, never a value (SEC-17). */
export function formatRows(rows: number | null | undefined): string {
  if (rows === null || rows === undefined || !Number.isFinite(rows)) return "—";
  return `${rows.toLocaleString()} ${rows === 1 ? "row" : "rows"}`;
}

/**
 * The transformations in dependency order — what the schedule editor shows and
 * the order the runner merges selectors into the bundle.
 *
 * Kahn's algorithm with the name as the tie-break, so the order is stable
 * between renders. The server refuses a cycle on write, but a list is read by
 * whoever holds `dbt_jobs.read`, so a cycle that reached the database by
 * another route still renders: whatever is left when no node is free is
 * appended in name order rather than dropped.
 */
export function orderByDependencies(rows: DbtJobView[]): DbtJobView[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const indegree = new Map<string, number>();
  const dependants = new Map<string, string[]>();
  for (const row of rows) {
    const known = row.dependsOn.filter((id) => byId.has(id));
    indegree.set(row.id, known.length);
    for (const dependency of known) {
      dependants.set(dependency, [...(dependants.get(dependency) ?? []), row.id]);
    }
  }
  const byName = (a: DbtJobView, b: DbtJobView) => a.name.localeCompare(b.name);
  const free = rows.filter((row) => indegree.get(row.id) === 0).sort(byName);
  const ordered: DbtJobView[] = [];
  const placed = new Set<string>();
  while (free.length > 0) {
    const next = free.shift() as DbtJobView;
    ordered.push(next);
    placed.add(next.id);
    for (const id of dependants.get(next.id) ?? []) {
      const remaining = (indegree.get(id) ?? 0) - 1;
      indegree.set(id, remaining);
      if (remaining === 0) {
        free.push(byId.get(id) as DbtJobView);
        free.sort(byName);
      }
    }
  }
  const leftover = rows.filter((row) => !placed.has(row.id)).sort(byName);
  return [...ordered, ...leftover];
}

/**
 * The first character a selector carries that dbt's grammar does not, or null.
 *
 * The drawer names the character rather than the rule, because "`;` is not
 * allowed" tells the user which key they hit and the rule alone does not.
 */
export function firstDisallowedSelectorCharacter(selector: string): string | null {
  if (selector.length === 0 || DBT_SELECTOR_CHARSET.test(selector)) return null;
  for (const character of selector) {
    if (!DBT_SELECTOR_CHARSET.test(character)) return character;
  }
  return null;
}

/**
 * The model a selector names DIRECTLY, or null.
 *
 * The FALLBACK for a graph with no `selection` (the run's bundle could not be
 * read): `daily_revenue`, `+daily_revenue`, `daily_revenue+` and
 * `@daily_revenue` name a model; anything else cannot be attributed without
 * the server's evaluation, and the row shows the RUN-LEVEL value and says so.
 */
export function selectorTargetModel(selector: string | null): string | null {
  if (!selector) return null;
  // Round 8: a hyphen may sit inside a name (`stg-orders`), never at its start.
  const match = /^[+@]?([A-Za-z0-9_][A-Za-z0-9_-]*)\+?$/.exec(selector.trim());
  return match?.[1] ?? null;
}

export interface TestCounts {
  ok: number;
  failed: number;
  skipped: number;
  /** #1207 round 2: tests with no result in the last run (Run tests off), never "skipped". */
  notRun: number;
}

export const NO_TEST_COUNTS: Readonly<TestCounts> = { ok: 0, failed: 0, skipped: 0, notRun: 0 };

/**
 * Tests counted by outcome: `pass` and `warn` are ok, `fail` and `error` failed, any other
 * status (`skipped`) skipped, and a test with no status (it did not run) not run. Skipped is
 * never folded into ok: a model whose tests were skipped after a failed dependency has not
 * "passed". #1207 round 2: a test that did not run is never called skipped, which on this page
 * means skipped after an upstream failure.
 */
export function testCounts(tests: TransformationGraphTest[]): TestCounts {
  const counts: TestCounts = { ...NO_TEST_COUNTS };
  for (const test of tests) {
    if (test.status === null) counts.notRun += 1;
    else if (test.status === "pass" || test.status === "warn") counts.ok += 1;
    else if (test.status === "fail" || test.status === "error") counts.failed += 1;
    else counts.skipped += 1;
  }
  return counts;
}

/**
 * #1207 round 2: a model's tests in one line, for the node dialog and the Tests panel. Tests with
 * no result read "Tests did not run in the last run.", never "0 passed". Round 3: the Tests panel
 * and the "This dbt job" tile (`testCountsPhrase`) read the same counts through `testCountsParts`.
 */
export function testCountsLine(counts: TestCounts): string {
  const parts = testCountsParts(counts);
  if (parts === null) {
    return counts.notRun > 0 ? "Tests did not run in the last run." : "No tests.";
  }
  return parts.join(" · ");
}

/** Whether no test has a result: every one did not run, or there is none. */
export function noTestResults(counts: TestCounts): boolean {
  return counts.ok + counts.failed + counts.skipped === 0;
}

/**
 * The counts' parts ("2 passed", "0 failed", then "1 skipped" and "1 did not run" when there are
 * any), or null when no test has a result.
 */
function testCountsParts(counts: TestCounts): string[] | null {
  if (noTestResults(counts)) return null;
  const parts = [`${counts.ok} passed`, `${counts.failed} failed`];
  if (counts.skipped > 0) parts.push(`${counts.skipped} skipped`);
  if (counts.notRun > 0) parts.push(`${counts.notRun} did not run`);
  return parts;
}

/**
 * #1207 round 3: the tests for a joined detail line (the "This dbt job" tile): the same counts as
 * `testCountsLine`, lower case, ending in "tests" ("6 passed · 1 failed · 2 skipped tests"), and
 * "tests did not run" when none has a result. Null only when there is no test at all.
 */
export function testCountsPhrase(counts: TestCounts): string | null {
  const parts = testCountsParts(counts);
  if (parts === null) return counts.notRun > 0 ? "tests did not run" : null;
  const notRun = counts.notRun > 0 ? parts.pop() : undefined;
  const line = `${parts.join(" · ")} tests`;
  return notRun ? `${line} · ${notRun}` : line;
}

/** The tests that cover ONE node, counted by outcome. */
export function testCountsFor(tests: TransformationGraphTest[], nodeId: string): TestCounts {
  return testCounts(tests.filter((test) => test.dependsOn.includes(nodeId)));
}

/**
 * Whether the runner would include this row in a run: enabled AND carrying a
 * selector. The runner's contract is "a blank `dbt_selector` is dropped, never
 * widened" (`runner/steps/transform.ts`): a selector-less row is left out with
 * `TRANSFORM_MODEL_SKIPPED`, never expanded to the whole project.
 */
export function isRunnable(row: DbtJobView): boolean {
  return runNowOffReason(row) === null;
}

/**
 * #1293: why one dbt job's Run now is switched off, or null when it can run. The same rule as
 * `isRunnable`: the runner drops a disabled job and a blank selector, never widening to the
 * whole project, so a Run now on either would start a run with nothing in it.
 */
export function runNowOffReason(row: DbtJobView, canWrite = true): string | null {
  // A reader without `dbt_jobs.write` cannot enable or edit a job, so the sentence names who can.
  if (!row.enabled) {
    return canWrite
      ? "Switched off because this dbt job is disabled. Enable it first."
      : "Switched off because this dbt job is disabled. An instance admin or above can enable it.";
  }
  if (!row.dbtSelector?.trim()) {
    return canWrite
      ? "Switched off because this dbt job has no selector. Give it one first."
      : "Switched off because this dbt job has no selector. An instance admin or above can give it one.";
  }
  return null;
}

/**
 * #1293: why Run now is off for the whole page, before any one job is looked at: the dbt project
 * has not loaded, could not be read, or has no repository bound. The same gate as the list's
 * "Run all dbt jobs now", so the dialog never opens to say "not bound" about a binding that has
 * not answered yet. Null when the page can start a run.
 */
export function runNowPageBlock(
  graphState: "loading" | "ready" | "error",
  unbound: boolean,
): string | null {
  if (graphState === "loading") return "Switched off until the dbt project has loaded.";
  if (graphState === "error") {
    return "Switched off because the dbt project could not be read. Refresh to try again.";
  }
  return unbound ? "Switched off until an instance admin or above sets the dbt repository." : null;
}

/**
 * #841: the step a screen describes, from the newest steps first. A step still queued or running
 * has no outcome yet, so while the newest is in flight the newest FINISHED step of the page is
 * shown (succeeded, failed, skipped or cancelled, scheduled or manual), and `inFlight` names the
 * newer one. The graph is never taken for it: its run is the last that launched dbt, and a later
 * skipped or failed step must not hide behind it. A page with no finished step shows the newest.
 */
export function lastFinishedStep(steps: readonly DbtJobRunRow[]): {
  shown: DbtJobRunRow | null;
  inFlight: DbtJobRunRow | null;
} {
  const newest = steps[0] ?? null;
  if (!newest || !transformStepPresentation(newest).isActive) {
    return { shown: newest, inFlight: null };
  }
  const finished = steps.find((step) => !transformStepPresentation(step).isActive);
  return finished ? { shown: finished, inFlight: newest } : { shown: newest, inFlight: null };
}

/** "a newer run is queued" or "a newer run is running", for a step in flight. */
export function newerRunWords(inFlight: DbtJobRunRow): string {
  return `a newer run is ${transformStepPresentation(inFlight).label.toLowerCase()}`;
}

/**
 * Whether a row was part of the given transform step. The step names the rows
 * it ran (`dbtJobNames`); when it names none (an older step, or one
 * that skipped before planning), the row's runnable state now is the best
 * available answer. A row created or enabled after the step must never be
 * shown with that step's outcome.
 */
export function ranInStep(row: DbtJobView, step: DbtJobRunRow | null): boolean {
  if (!step) return false;
  if (step.dbtJobNames.length === 0) return isRunnable(row);
  return step.dbtJobNames.includes(row.name);
}

/**
 * What the graph's run did to the nodes ONE transformation selected.
 *
 * `nodeIds` is every node the selector selected (sources included, as dbt's
 * own `ls` lists them); `models` the ones dbt builds (never a source), in the
 * graph's order; `tests` every test on any selected node. `outcome` is the
 * worst dbt status among the models the run reported (an error outranks a
 * skip, a skip outranks a success), null when none was reported.
 */
export interface AttributedRun {
  basis: "selector" | "sole" | "direct";
  nodeIds: Set<string>;
  models: TransformationGraphNode[];
  tests: TransformationGraphTest[];
  testCounts: TestCounts;
  outcome: Presentation | null;
  rows: number | null;
}

/** How bad a reported node's outcome is: a failure, then a skip, then anything else, then a success. */
function outcomeRank(node: TransformationGraphNode): number {
  if (node.failed || node.status === "error" || node.status === "fail") return 3;
  if (node.status === "skipped") return 2;
  return node.status === "success" ? 0 : 1;
}

function worstOutcome(models: TransformationGraphNode[]): Presentation | null {
  const reported = models.filter((node) => node.status !== null);
  // The first of the worst wins a tie, as each later node replaces it only when strictly worse.
  const worst = reported.reduce<TransformationGraphNode | null>(
    (a, b) => (a === null || outcomeRank(b) > outcomeRank(a) ? b : a),
    null,
  );
  if (worst === null) return null;
  return nodeStatusPresentation(worst.status);
}

function attributionOf(
  basis: AttributedRun["basis"],
  nodeIds: Set<string>,
  graph: TransformationGraph,
): AttributedRun {
  // Models only: `dbt run` builds models; a seed or snapshot the selector names is in `nodeIds`
  // (the outline) but is not counted as a model it built (review N-6).
  const models = graph.nodes.filter(
    (node) => nodeIds.has(node.uniqueId) && node.resourceType === "model",
  );
  const tests = graph.tests.filter((test) => test.dependsOn.some((id) => nodeIds.has(id)));
  return {
    basis,
    nodeIds,
    models,
    tests,
    testCounts: testCounts(tests),
    outcome: worstOutcome(models),
    rows: sumNodeRows(models),
  };
}

/**
 * The graph's selection entry for a transformation: undefined when the graph
 * has no selection (bundle unreadable) or the transformation was not in the
 * graph's run.
 */
export function selectionFor(
  dbtJobId: string,
  graph: TransformationGraph | undefined,
): DbtJobSelection | undefined {
  return graph?.selection?.find((entry) => entry.dbtJobId === dbtJobId);
}

/**
 * What the graph's run did to the nodes this transformation selected, or null
 * when that is not known.
 *
 * With a `selection` (the server evaluated each selector against the manifest,
 * `selectedBy` on each node), a transformation is attributed when its entry is
 * `known` — `tag:` and `path:` included — and never when it is not in the run
 * or its selector could not be evaluated. Without one, the direct-name
 * fallback (`selectorTargetModel`) is all there is.
 */
export function attributeToGraph(
  transformation: DbtJobView,
  graph: TransformationGraph | undefined,
): AttributedRun | null {
  if (!graph) return null;
  if (graph.selection) {
    const entry = selectionFor(transformation.id, graph);
    if (!entry?.known || !entry.basis) return null;
    const ids = new Set(
      graph.nodes
        .filter((node) => node.selectedBy.includes(transformation.id))
        .map((node) => node.uniqueId),
    );
    return attributionOf(entry.basis, ids, graph);
  }
  const target = selectorTargetModel(transformation.dbtSelector);
  if (!target) return null;
  const node = graph.nodes.find((n) => n.resourceType === "model" && n.name === target);
  if (!node) return null;
  return attributionOf("direct", new Set([node.uniqueId]), graph);
}

/** A dbt job's own last run, as `attributeToOwnRun` reads it. */
export interface OwnRunAttribution {
  runId: string;
  /** When its own run finished (the run record), else its models' newest build in it; null if neither. */
  at: string | null;
  /** True when `at` is the run record's finish; false when it is a model's newest build time. */
  atIsFinish: boolean;
  /** How the run itself finished; null from an older server that did not send it. */
  status: DbtJobOwnRunStatus | null;
  /** The run's exit class; null when it has none or an older server did not send it. */
  exitClass: string | null;
  /** False for a run that failed before it launched dbt; null when an older server did not say. */
  launched: boolean | null;
  /** Whether the run asked dbt for tests; null when not recorded. */
  testsAsked: boolean | null;
  /** How many dbt jobs its run ran; null when an older server did not say. */
  jobsInRun: number | null;
  /**
   * What its models and tests did IN THAT RUN (each node and test by its newest result only
   * when that result is the run's own), when its selector was evaluated (`known`); else null.
   */
  attributed: AttributedRun | null;
  /** The same nodes by their newest result from ANY run (another run may have built them since). */
  newest: AttributedRun | null;
}

/** The graph with every node and test read by its newest result, of `runId` alone when given. */
function graphByNewest(graph: TransformationGraph, runId: string | null): TransformationGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      const built = node.lastBuilt ?? null;
      const last = built && (runId === null || built.runId === runId) ? built : null;
      return {
        ...node,
        status: last ? last.status : null,
        failed: last ? isDbtFailedStatus(last.status) : false,
        rowsAffected: last ? last.rowsAffected : null,
      };
    }),
    tests: graph.tests.map((test) => {
      const result = test.lastResult ?? null;
      if (runId === null) {
        return {
          ...test,
          status: result ? result.status : test.status,
          failures: result ? result.failures : test.failures,
        };
      }
      const own = result && result.runId === runId ? result : null;
      return { ...test, status: own ? own.status : null, failures: own ? own.failures : null };
    }),
  };
}

/**
 * Walk 2026-10-02, D4: what a transformation the graph's run did not name last did, by its OWN
 * last run (`graph.ownRuns`, the server's evaluation of its selector against the project): the
 * nodes it selects, each read by its newest build (`lastBuilt`) and each test by its newest
 * result (`lastResult`), not by the graph's run. #1074: an own run whose selector could not be
 * evaluated (it failed before dbt listed the project, say) is still read, by the run record's
 * `status` and `finishedAt` alone. #1074 review: `attributed` counts only the results that
 * run produced, so a later run's builds and tests are never credited to it (`newest` keeps them
 * apart). Null when it never ran, or when an older server sent neither an evaluation nor a status.
 */
export function attributeToOwnRun(
  transformation: DbtJobView,
  graph: TransformationGraph | undefined,
): OwnRunAttribution | null {
  const own = graph?.ownRuns?.find((entry) => entry.dbtJobId === transformation.id);
  if (!graph || !own) return null;
  const record = {
    runId: own.runId,
    status: own.status ?? null,
    exitClass: own.exitClass ?? null,
    launched: own.launched ?? null,
    testsAsked: own.testsAsked ?? null,
    jobsInRun: own.jobsInRun ?? null,
  };
  const finishedAt = own.finishedAt ?? null;
  if (!own.known || !own.basis) {
    if (!record.status) return null;
    return {
      ...record,
      at: finishedAt,
      atIsFinish: finishedAt !== null,
      attributed: null,
      newest: null,
    };
  }
  const ids = new Set(
    graph.nodes
      .filter((node) => node.selectedBy.includes(transformation.id))
      .map((node) => node.uniqueId),
  );
  const attributed = attributionOf(own.basis, ids, graphByNewest(graph, own.runId));
  const newest = attributionOf(own.basis, ids, graphByNewest(graph, null));
  const times = attributed.models
    .map((node) => node.lastBuilt?.at ?? null)
    .filter((at): at is string => at !== null)
    .sort(byCodeUnit);
  return {
    ...record,
    at: finishedAt ?? times.at(-1) ?? null,
    atIsFinish: finishedAt !== null,
    attributed,
    newest,
  };
}

/** The exit classes of a dbt run that ended before its test phase (`transform/skydata_dbt/run.py`). */
const BEFORE_TESTS_EXIT_CLASSES: ReadonlySet<string> = new Set([
  "config",
  "auth",
  "clone",
  "deps",
  "compile",
  "run",
  "stale",
]);

/** The exit classes of a dbt run that ended before any model ran: `BEFORE_TESTS_EXIT_CLASSES` but `run`. */
const BEFORE_MODELS_EXIT_CLASSES: ReadonlySet<string> = new Set([
  "config",
  "auth",
  "clone",
  "deps",
  "compile",
  "stale",
]);

/**
 * #1083 gate: whether a run certainly stopped before dbt ran any model: it never launched dbt, or
 * its exit class is a phase before models. A run that failed in the run phase ran its models, so
 * "its models did not run" is said only when this holds, never because a later run rebuilt them.
 */
function stoppedBeforeModels(exitClass: string | null, launched: boolean | null): boolean {
  if (launched === false) return true;
  return exitClass !== null && BEFORE_MODELS_EXIT_CLASSES.has(exitClass);
}

/**
 * #1074 review: whether a step that failed or was cancelled certainly stopped before dbt's test
 * phase: it never launched dbt, or its exit class is one of the phases before tests. False when
 * that is not known (a timeout, an infrastructure error, a cancel: the phase is not recorded).
 */
export function stoppedBeforeTests(
  status: string | null,
  exitClass: string | null,
  launched: boolean | null,
): boolean {
  if (status !== "failed" && status !== "cancelled") return false;
  if (launched === false) return true;
  return exitClass !== null && BEFORE_TESTS_EXIT_CLASSES.has(exitClass);
}

/**
 * #1074: a job's own last run in words, every word from the run record and its line from what
 * that run itself did (`attributed`, never a later run's builds), so the badge, the line and the
 * time describe one run. A failed run is named by its exit class as the Runs list names it
 * (`stepStatusPresentation`), with the models' line when it says more ("1 of its models failed",
 * "its models did not run"). A run several dbt jobs shared that failed while this job's models
 * all built reads Succeeded and says the run failed in another dbt job. A succeeded run reads
 * Succeeded, with the models' line when they agree. `line` is null when there is nothing to add.
 */
export function ownRunWords(own: OwnRunAttribution): {
  word: string;
  line: string | null;
  color: SkyBadgeColor;
} {
  const words = own.attributed ? attributedRunWords(own.attributed) : null;
  const modelsColor = (attributed: AttributedRun): SkyBadgeColor =>
    attributed.models.length === 0 ? "subtle" : (attributed.outcome?.color ?? "subtle");
  if (own.status === "failed") {
    const shared = own.jobsInRun !== null && own.jobsInRun > 1;
    if (shared && own.attributed && words && own.attributed.testCounts.failed === 0) {
      if (words.word === "Succeeded") {
        return {
          word: words.word,
          line: `${words.line} · its run failed in another dbt job`,
          color: modelsColor(own.attributed),
        };
      }
      if (words.word === "Skipped") {
        return { word: words.word, line: words.line, color: modelsColor(own.attributed) };
      }
    }
    const presented = stepStatusPresentation("failed", own.exitClass);
    // "Not run" means no node's newest build is this run's: a later run may have rebuilt them, so
    // it reads "its models did not run" only when the run record says so (#1083 gate).
    const notRun = words?.word === "Not run" && stoppedBeforeModels(own.exitClass, own.launched);
    const line = words && (words.word === "Failed" || notRun) ? words.line : null;
    return { word: presented.label, line, color: presented.color };
  }
  if (own.status === "cancelled") {
    const presented = stepStatusPresentation("cancelled", own.exitClass);
    return { word: presented.label, line: null, color: presented.color };
  }
  if (own.status === "succeeded") {
    if (own.attributed && words && (words.word === "Succeeded" || words.word === "No models")) {
      return { word: words.word, line: words.line, color: modelsColor(own.attributed) };
    }
    return { word: "Succeeded", line: null, color: "success" };
  }
  // An older server sent no status: the models' words, as before #1074.
  if (own.attributed && words) {
    return { word: words.word, line: words.line, color: modelsColor(own.attributed) };
  }
  return { word: "Not run", line: null, color: "subtle" };
}

/**
 * A Last tests reading: a count badge with its line, or a muted sentence. `testsOff` marks a
 * sentence that says the job's tests were off (#1386: such a job is not listed as one that ran
 * tests).
 */
export type TestsReading =
  | { kind: "counts"; ok: number; failed: number; scope: "its own run" }
  | { kind: "text"; text: string; testsOff?: true };

/** #1074 QA 10/16: a run that recorded nothing about tests, of a job with tests off today. */
const TESTS_OFF_NOW: TestsReading = { kind: "text", text: "Tests off", testsOff: true };
const TESTS_OFF_IN_OWN_RUN: TestsReading = {
  kind: "text",
  text: "Tests off in its own run",
  testsOff: true,
};

/**
 * #1074 review: a job's own run's tests in words, for the Jobs view and the Overview card alike.
 *
 * The counts are the run's own results ("its own run") and nothing else. #1386: a result another
 * run produced on its models (a later run) is never shown as its counts, and neither is another
 * dbt job's tests in a run they shared. A run records whether it asked for tests for the whole
 * step (`testsAsked`), never per job, so: a run that asked for none reads "Tests off in its own
 * run" before any count; a run this job shared with other jobs (`jobsInRun` above 1), of a job
 * with Run tests off today, reads "Tests off", since the tests that ran on its models were the
 * other jobs' (a stated residual: a job switched off after such a run reads Tests off too); a run
 * it ran alone reads its counts whatever today's setting is.
 *
 * A run that failed or was cancelled with no test result says why from the run record: its tests
 * failed (exit class `test`), it stopped before tests ran (it never launched, or failed in a phase
 * before tests), or the result is not known. `runTestsNow` (today's setting) is otherwise read
 * only when the run did not record whether it asked for tests: such a run, with the job's tests
 * off today, reads "Tests off" (#1074 QA 10/16), and a run that recorded it asked for tests never
 * does. A run that asked for tests, with no result of its own while a later run's results sit on
 * its models, reads "Not known for its own run". Null when there is nothing to say.
 */
export function ownRunTestsReading(
  own: OwnRunAttribution,
  runTestsNow: boolean,
): TestsReading | null {
  if (own.testsAsked === false) return TESTS_OFF_IN_OWN_RUN;
  const shared = own.jobsInRun !== null && own.jobsInRun > 1;
  // #1386: in a shared run the tests on its models were the other jobs' while its own were off.
  if (shared && !runTestsNow) return TESTS_OFF_NOW;
  const counts = own.attributed?.testCounts;
  if (counts && counts.ok + counts.failed > 0) {
    return {
      kind: "counts",
      ok: counts.ok,
      failed: counts.failed,
      scope: "its own run",
    };
  }
  if (own.status === "failed" || own.status === "cancelled") {
    if (own.exitClass === "test") return { kind: "text", text: "Tests failed · its own run" };
    const asked = own.testsAsked ?? runTestsNow;
    if (!asked) return TESTS_OFF_NOW;
    if (stoppedBeforeTests(own.status, own.exitClass, own.launched)) {
      return { kind: "text", text: "Not run · its own run stopped before tests ran" };
    }
    return {
      kind: "text",
      text:
        own.status === "cancelled"
          ? "Not known · its own run was cancelled"
          : "Not known · its own run failed",
    };
  }
  // The run did not record whether it asked for tests: today's setting answers.
  if (own.testsAsked == null && !runTestsNow) return TESTS_OFF_NOW;
  // #1386: its own run asked for tests, yet the newest results on its models are another run's.
  // Those are that run's counts, not this job's, so the reading says its own are not known.
  const other = own.newest?.testCounts;
  if (other && other.ok + other.failed > 0) {
    return { kind: "text", text: "Not known for its own run" };
  }
  return null;
}

/**
 * #1386: whether the step's test results on a job's models may be read as that job's own. A step
 * records whether it asked for tests for the whole pass (`testsRequested`), never per job. A step
 * that asked for none ran no job's tests. A step that ran this job alone ran its tests when it ran
 * any, whatever the job's Run tests setting is today, so a job switched off after it still reads
 * its failures. In a step shared with other jobs the record cannot say whose tests ran on its
 * models, so today's setting decides: a stated residual (a job switched off after a shared step
 * reads Tests off, one switched on after a tests-off step reads the counts on its models).
 */
export function stepTestCountsAreOwn(row: DbtJobView, step: DbtJobRunRow): boolean {
  if (step.testsRequested === false) return false;
  const alone = step.dbtJobNames.length === 1 && step.dbtJobNames[0] === row.name;
  return alone || row.runTests;
}

/** #1386: a job's tests in the step it ran in, as data for the card and the Jobs grid alike. */
export type StepTestsReading =
  | { kind: "counts"; ok: number; failed: number; attributed: AttributedRun }
  | { kind: "testsOff" }
  | { kind: "stoppedBeforeTests" }
  | { kind: "noTests" }
  | { kind: "runLevel"; failed: number | null };

/**
 * #1386: a job's tests in the step it ran in: its models' counts when they are its own
 * (`stepTestCountsAreOwn`), else "Tests off" when the step asked for none or the job's tests are
 * off; then "the run stopped before tests ran", "No tests on its models" (a known selection with
 * no test result), or the whole run's count when the graph cannot attribute (`attributed` null).
 */
export function stepTestsReading(
  row: DbtJobView,
  step: DbtJobRunRow,
  attributed: AttributedRun | null,
): StepTestsReading {
  if (step.testsRequested === false) return { kind: "testsOff" };
  const counts = attributed?.testCounts;
  if (attributed && counts && counts.ok + counts.failed > 0 && stepTestCountsAreOwn(row, step)) {
    return { kind: "counts", ok: counts.ok, failed: counts.failed, attributed };
  }
  if (!row.runTests) return { kind: "testsOff" };
  if (attributed && stepStoppedBeforeTests(step)) return { kind: "stoppedBeforeTests" };
  if (attributed) return { kind: "noTests" };
  return { kind: "runLevel", failed: step.testsFailed };
}

/**
 * #1386: a job's Last tests, the one decision the Overview card and the Jobs grid both read: its
 * own run's reading when the step did not name it (`ownRunTestsReading`), else its reading in that
 * step (`stepTestsReading`, attributed only when the graph is that step's run).
 */
export type JobTestsReading =
  | { path: "own"; own: OwnRunAttribution | null; reading: TestsReading | null }
  | { path: "step"; reading: StepTestsReading };

export function jobTestsReading(
  row: DbtJobView,
  step: DbtJobRunRow | null,
  graph: TransformationGraph | undefined,
): JobTestsReading {
  if (!step || !ranInStep(row, step)) {
    const own = attributeToOwnRun(row, graph);
    return { path: "own", own, reading: own ? ownRunTestsReading(own, row.runTests) : null };
  }
  const graphIsStep = Boolean(graph?.run && graph.run.runId === step.runId);
  const attributed = graphIsStep ? attributeToGraph(row, graph) : null;
  return { path: "step", reading: stepTestsReading(row, step, attributed) };
}

/**
 * #1386: whether a job's reading says something about tests it ran (counts, its tests failed,
 * stopped before tests, not known), so a disabled job is still listed on the Overview card. A
 * reading of tests off, or none, says nothing of the kind.
 */
export function readsTestsItRan(reading: JobTestsReading): boolean {
  if (reading.path === "step") return reading.reading.kind === "counts";
  if (!reading.reading) return false;
  return reading.reading.kind === "counts" || !reading.reading.testsOff;
}

/**
 * #1074 review: a job the graph's step DID run, when that step failed or was cancelled before
 * dbt reported any of its models (a known selection, every model unreported): the step's own
 * word (`transformStepPresentation`), never a grey "Not run" under a failed run. Null otherwise.
 */
export function stoppedStepWords(
  attributed: AttributedRun,
  step: DbtJobRunRow,
): { word: string; line: string; color: SkyBadgeColor } | null {
  if (step.stepStatus !== "failed" && step.stepStatus !== "cancelled") return null;
  if (attributed.models.length === 0 || attributed.outcome !== null) return null;
  const presented = transformStepPresentation(step);
  // #1444: a cancelled step that started may have built models dbt never reported, so it never
  // claims none ran. One cancelled before dbt started sent nothing.
  const line =
    step.stepStatus === "cancelled" && step.startedAt !== null
      ? "the run was stopped, dbt did not report which of its models finished"
      : "its models did not run";
  return { word: presented.label, line, color: presented.color };
}

/**
 * #1074 review: whether a step the job ran in stopped before dbt's tests while the step asked for
 * them (`testSelect`), so a job with no test result reads "Not run" with the reason, never
 * "No tests on its models".
 */
export function stepStoppedBeforeTests(step: DbtJobRunRow): boolean {
  return step.testSelect !== null && stoppedBeforeTests(step.stepStatus, step.exitClass, true);
}

/**
 * Round 6 (W7, owner): a row's Last run in words. The badge is the outcome of the models it
 * selected ("Failed", "Skipped", "Succeeded"), never dbt's lower-case status; the line says what
 * that means for this row: "2 of its models failed", "an upstream dbt job failed" (its
 * models were skipped and none of them failed), or what it built and how many rows.
 */
export function attributedRunWords(attributed: AttributedRun): { word: string; line: string } {
  const models = attributed.models;
  if (models.length === 0) return { word: "No models", line: "its selector matched no model" };
  const failed = models.filter(
    (node) => node.failed === true || node.status === "error" || node.status === "fail",
  ).length;
  if (failed > 0) return { word: "Failed", line: `${failed} of its models failed` };
  if (models.some((node) => node.status === "skipped")) {
    return { word: "Skipped", line: "an upstream dbt job failed" };
  }
  if (attributed.outcome === null) return { word: "Not run", line: "its models did not run" };
  const only = models.length === 1 ? models[0] : undefined;
  const what = only ? only.name : `${models.length} models`;
  const rows = attributed.rows === null ? "" : ` · ${formatRows(attributed.rows)}`;
  return {
    word: attributed.outcome.color === "success" ? "Succeeded" : attributed.outcome.label,
    line: `${what} built${rows}`,
  };
}

/**
 * #1093: the rows a set of nodes wrote, by the one rule (`dbtNodeRows`): a view or an ephemeral
 * node writes none (the 1 Snowflake reports for a view is its DDL statement), so only table,
 * incremental and snapshot counts add up. Null when no node reported a count. The server already
 * sends null for those nodes; the rule is applied here again so the job's total and Rows written
 * can never disagree.
 */
function sumNodeRows(nodes: readonly TransformationGraphNode[]): number | null {
  return sumRowCounts(nodes.map((node) => dbtNodeRows(node.materialized, node.rowsAffected)));
}

/**
 * #1093: the one sum of dbt row counts: the reported counts only, null when there is none, so a
 * run whose models are all views reads "—", never "0 rows". Rows written, the job's total and the
 * History view's run line all add up through it.
 */
export function sumRowCounts(counts: readonly (number | null | undefined)[]): number | null {
  const reported = counts.filter((rows): rows is number => typeof rows === "number");
  return reported.length === 0 ? null : reported.reduce((sum, rows) => sum + rows, 0);
}

/** The whole-run row count: the rows the run's model nodes wrote (`sumNodeRows`). */
export function rowsWritten(graph: TransformationGraph): number | null {
  return sumNodeRows(graph.nodes.filter((node) => node.resourceType === "model"));
}

/** The model a test covers: the first model node in its `dependsOn`, or "other". */
export function testGroupKey(
  test: TransformationGraphTest,
  nodesById: Map<string, TransformationGraphNode>,
): string {
  for (const id of test.dependsOn) {
    const node = nodesById.get(id);
    if (node && node.resourceType !== "source") return node.uniqueId;
  }
  return test.dependsOn[0] ?? "other";
}

/**
 * The `TRANSFORM_NOT_READY` details as a checklist the operator can act on.
 *
 * Each field the server pre-flights maps to the screen that fixes it. The
 * server's own `issue` sentence is kept as the body: the client never composes
 * a cause of its own (SEC-17), only says where to go.
 */
export interface NotReadyItem {
  field: string;
  label: string;
  issue: string;
  /** Where the fix lives: the Destination screen, or this screen. */
  fixOn: "destination" | "transformations" | "deployment";
}

const NOT_READY_LABELS: Record<string, { label: string; fixOn: NotReadyItem["fixOn"] }> = {
  destinationType: { label: "Destination type", fixOn: "destination" },
  snowflakeAccount: { label: "Snowflake account", fixOn: "destination" },
  dbtRepoUrl: { label: "dbt repository", fixOn: "destination" },
  dbtJobIds: { label: "Enabled dbt jobs", fixOn: "transformations" },
  // Round 6 (T3): a run of chosen models names a model the last manifest does not list.
  models: { label: "Chosen models", fixOn: "transformations" },
};

export function notReadyChecklist(details: ErrorDetail[]): NotReadyItem[] {
  return details.map((detail) => {
    const field = detail.field ?? "unknown";
    const known = NOT_READY_LABELS[field] ?? { label: field, fixOn: "deployment" as const };
    return { field, label: known.label, issue: detail.issue, fixOn: known.fixOn };
  });
}

/**
 * The runner's sentence, closed with a full stop when it has no end punctuation
 * of its own (the R3 punctuation rule): its step error ends in a count ("... 2
 * skipped because a model they depend on failed" since round 6, "did not
 * succeed" before) with no stop, and a sentence is appended after it on the detail page's
 * failure bar and the History tab's latest-run bar.
 */
export function asSentence(text: string): string {
  const trimmed = text.trimEnd();
  return /[.?!]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * One cell of the model history (session 7, canvas 18): the outcome in words,
 * a glyph and a shape, so colour is never the only signal. A failed cell has
 * a thicker border, a model that was not built a dashed one, and a status
 * outside dbt's set reads as exactly that rather than as a guess.
 */
export interface HistoryOutcomePresentation extends Presentation {
  /** A glyph that carries the outcome without colour (decorative; the label is the name). */
  glyph: string;
  /** Semantic token classes for the cell: background, text and border shape. */
  cellClass: string;
}

const HISTORY_OUTCOMES: Record<DbtJobHistoryOutcomeKind, HistoryOutcomePresentation> = {
  succeeded: {
    label: "Succeeded",
    color: "success",
    isActive: false,
    glyph: "✓",
    cellClass: "border border-success-border bg-success-subtle text-success-subtle-foreground",
  },
  failed: {
    label: "Failed",
    color: "danger",
    isActive: false,
    glyph: "✕",
    cellClass:
      "border-2 border-destructive-border bg-destructive-subtle text-destructive-subtle-foreground",
  },
  skipped: {
    label: "Skipped",
    color: "warning",
    isActive: false,
    glyph: "↷",
    cellClass: "border border-warning-border bg-warning-subtle text-warning-subtle-foreground",
  },
  not_reported: {
    label: "Not built",
    color: "subtle",
    isActive: false,
    glyph: "○",
    cellClass: "border border-dashed border-border bg-muted text-muted-foreground",
  },
  running: {
    label: "Running",
    color: "informative",
    isActive: true,
    glyph: "…",
    cellClass: "border border-info-border bg-info-subtle text-info-subtle-foreground",
  },
  unknown: {
    label: "Status not recognised",
    color: "subtle",
    isActive: false,
    glyph: "?",
    cellClass: "border border-dotted border-border bg-secondary text-muted-foreground",
  },
};

export function historyOutcomePresentation(
  outcome: DbtJobHistoryOutcomeKind,
): HistoryOutcomePresentation {
  return HISTORY_OUTCOMES[outcome] ?? HISTORY_OUTCOMES.unknown;
}

/**
 * #1214, #986: the run a dbt step row belongs to, named the way the Runs list names it: by the
 * run's own start (`runStartedAt`), never the dbt step's (which is minutes later, after the clone
 * and the install), and a queued or cancelled run that never began has no start at all.
 */
export type RunTimes = Pick<
  DbtJobRunRow,
  "runStatus" | "createdAt" | "startedAt" | "stepStatus" | "exitClass" | "phase"
> &
  Partial<Pick<DbtJobRunRow, "runStartedAt">>;

/** The run's start for a row that has begun; the step's start, then the creation, as a last resort. */
export function runStartOf(row: RunTimes): string {
  return row.runStartedAt ?? row.startedAt ?? row.createdAt;
}

/**
 * #1207 (G1 review): a run's Started words on the dbt job page, as the Runs list and the History
 * view say them: "Not started" for a run that never began, else the run's own start.
 */
export function runStartText(
  row: RunTimes,
  zone: string,
  style: InstantStyle = "seconds",
  options: { named?: boolean } = {},
): string {
  return runNotStarted(row)
    ? NOT_STARTED_LABEL
    : formatInstant(runStartOf(row), zone, style, options);
}

/** True for a run that is still queued (or was cancelled while queued): it has no start time. */
export function runNotStarted(row: RunTimes): boolean {
  return isNotStarted({
    status: row.runStatus,
    startedAt: row.runStartedAt ?? row.startedAt ?? null,
  });
}
